import { useState, useEffect, useCallback, useRef } from "react";
import { Joyride, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { FRIENDS_MODE_ENABLED } from "@/contexts/ThemeContext";

const SKIP_BUTTONS = ["back", "close", "primary", "skip"];

const MAIN_TOUR_STEPS = [
  // ── EXISTING (updated copy) ──
  {
    target: '[data-testid="homepage"]',
    content: "You have three modes. Birthday Mode for your big day, Discover Mode for year-round deals, and Friends Mode to track birthdays of people you care about.",
    title: "Welcome to BirthdayDeaLight!",
    disableBeacon: true,
    placement: "center",
    friendsOnly: true,
  },
  {
    target: '[data-testid="homepage"]',
    content: "Two powerful modes. Birthday Mode for your big day, and Discover Mode for year-round deals.",
    title: "Welcome to BirthdayDeaLight!",
    disableBeacon: true,
    placement: "center",
    friendsDisabled: true,
  },
  {
    target: '[data-testid="mode-switcher"]',
    content: "Switch between Birthday, Discover, and Friends modes right here. The entire app shifts to match your mode.",
    friendsOnly: true,
  },
  {
    target: '[data-testid="mode-switcher"]',
    content: "Switch between Birthday and Discover modes right here. The entire app shifts to match your mode.",
    friendsDisabled: true,
    title: "Mode Switcher",
    placement: "bottom",
  },
  {
    target: '[data-testid="floating-search"]',
    content: "Search for deals near you. Try typing a restaurant name, category, or keyword like 'free coffee'.",
    title: "Search Deals",
    placement: "bottom",
  },
  {
    target: '[data-testid="filters-dropdown"]',
    content: "Filter by category, dietary needs, value, or deal type. Filters adapt to your active mode.",
    title: "Smart Filters",
    placement: "bottom",
  },

  // ── NEW: Discover Mode ──
  {
    target: '[data-testid="mode-switcher"]',
    content: "In Discover Mode the app shifts to an indigo theme. Browse all deals anytime, complete monthly challenges, and explore new cities — no birthday required.",
    title: "Discover Mode",
    placement: "bottom",
  },

  // ── NEW: Birthday Auto-Switch ──
  {
    target: '[data-testid="mode-switcher"]',
    content: "When your birthday is 30 days away the app automatically switches to Birthday Mode. You can always switch back manually anytime.",
    title: "Auto-Switch",
    placement: "bottom",
  },

  // ── EXISTING (fixed testid) ──
  {
    target: '[data-testid="nav-my-deals"]',
    content: "Track your claimed deals, favorites, and birthday countdown all in one place.",
    title: "My Deals",
    placement: "top",
  },
  {
    target: '[data-testid="nav-new"]',
    content: "Stay updated with new deals, verified offers, and city expansions.",
    title: "What's New",
    placement: "top",
  },
  {
    target: '[data-testid="nav-ranks"]',
    content: "See how you rank against other deal hunters on the leaderboard.",
    title: "Leaderboard",
    placement: "top",
  },

  // ── NEW: Gamification Points ──
  {
    target: '[data-testid="nav-ranks"]',
    content: "Earn points and badges for everything you do — claiming deals, verifying businesses, referring friends, and more. Check your progress on your profile.",
    title: "Points & Badges",
    placement: "top",
  },

  // ── NEW: Streak System ──
  {
    target: '[data-testid="nav-ranks"]',
    content: "Open the app daily to build a streak. 3-day streaks earn bonus points, and 7-day streaks unlock exclusive rewards. Don't break the chain!",
    title: "Daily Streaks",
    placement: "top",
  },

  // ── NEW: Monthly Challenges ──
  {
    target: '[data-testid="nav-ranks"]',
    content: "Every month brings a new challenge — claim from 5 new categories, verify 3 deals, or explore 2 new neighborhoods. Complete it for bonus XP and a special badge.",
    title: "Monthly Challenges",
    placement: "top",
  },

  // ── EXISTING (updated copy) ──
  {
    target: '[data-testid="nav-profile"]',
    content: "Manage your profile, dietary preferences, location, and see which badges you're closest to earning.",
    title: "Your Profile",
    placement: "top",
  },

  // ── NEW: Badge Chase ──
  {
    target: '[data-testid="nav-profile"]',
    content: "See exactly how close you are to your next badge right on your profile. The top 3 badges you're closest to earning are always visible.",
    title: "Badge Chase",
    placement: "top",
  },

  // ── NEW: Wishlist ──
  {
    target: '[data-testid="nav-profile"]',
    content: "Save deals to your Wishlist so friends and family know exactly what you want for your birthday. Your wishlist is shareable — it's your birthday gift registry for deals.",
    title: "Wishlist",
    placement: "top",
  },

  // ── NEW: City Explorer ──
  {
    target: '[data-testid="city-progress-section"], [data-testid="homepage"]',
    content: "Track which categories you've explored in your city. Claim from all 16 categories to complete your city map and earn the City Legend badge.",
    title: "City Explorer",
    placement: "center",
  },

  // ── Friends Mode ──
  {
    target: '[data-testid="mode-friends-pill"]',
    content: "Friends Mode is here! Track birthdays of people you care about, plan surprise birthday deal runs, and share countdown links.",
    title: "Friends Mode",
    placement: "bottom",
    friendsOnly: true,
  },

  // ── NEW: Offer Detail Deep-Dive ──
  {
    target: '[data-testid="homepage"]',
    content: "Tap any deal card to see the full details — redemption instructions, fine print, hours, location map, and user-verified status. Everything you need before walking in.",
    title: "Deal Details",
    placement: "center",
  },

  // ── NEW: Itinerary Builder ──
  {
    target: '[data-testid="nav-my-deals"]',
    content: "Once you save deals, they become stops on your birthday itinerary. The app builds an optimized route so you can hit every deal in the most efficient order.",
    title: "Birthday Itinerary",
    placement: "top",
  },

  // ── NEW: Smart Route ──
  {
    target: '[data-testid="nav-my-deals"]',
    content: "Your itinerary auto-sorts by distance and category balance. Drag to reorder, check off stops as you go, and share your route with friends joining you.",
    title: "Smart Route",
    placement: "top",
  },

  // ── NEW: Verification System ──
  {
    target: '[data-testid="homepage"]',
    content: "Help the community by verifying deals you've claimed. Did the business honor the deal? Your reports keep data fresh and earn you Verifier badges.",
    title: "Verify & Earn",
    placement: "center",
  },

  // ── NEW: Search Pro Tips ──
  {
    target: '[data-testid="floating-search"]',
    content: "Pro tip: Combine search with filters for powerful results. Try 'coffee' + 'no purchase required' to find the best hassle-free birthday coffees.",
    title: "Search Pro Tips",
    placement: "bottom",
  },

  // ── NEW: Share & Invite ──
  {
    target: '[data-testid="nav-profile"]',
    content: "Share your wishlist, itinerary, or individual deals with friends. When they sign up, you both earn bonus points. Birthday deals are better together!",
    title: "Share & Invite",
    placement: "top",
  },

  // ── EXISTING (final step, updated copy) ──
  {
    target: '[data-testid="homepage"]',
    content: "You're all set! Start exploring deals in your area. Switch between modes, chase badges, and happy hunting!",
    title: "You're Ready!",
    placement: "center",
    disableBeacon: true,
  },
].map(step => ({ ...step, buttons: SKIP_BUTTONS }));

const TOUR_STYLES = {
  options: {
    arrowColor: "#fff",
    backgroundColor: "#fff",
    overlayColor: "rgba(15, 23, 42, 0.6)",
    primaryColor: "#0ea5e9",
    textColor: "#1e293b",
    zIndex: 10000,
  },
  tooltipContainer: {
    textAlign: "left",
  },
  buttonNext: {
    backgroundColor: "#0ea5e9",
    borderRadius: "10px",
    color: "#fff",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 18px",
  },
  buttonBack: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 500,
  },
  buttonSkip: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  tooltip: {
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
  },
  tooltipTitle: {
    fontSize: "15px",
    fontWeight: 700,
    marginBottom: "6px",
  },
  tooltipContent: {
    fontSize: "13px",
    lineHeight: "1.5",
    color: "#475569",
  },
};

export function AppTour({ tourId = "main_tour" }) {
  const { user } = useAuth();
  const { setTourRunning } = useOnboarding();
  const [run, setRun] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const completedRef = useRef(false);
  const tourStartedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    // Skip tour for vendor accounts — they've already onboarded through vendor flow
    if (user.role === "vendor") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/tour-state`);
        const completed = res.data.completed_tours || [];
        if (!completed.includes(tourId) && !cancelled) {
          setTimeout(() => {
            setRun(true);
            setTourRunning(true);
            tourStartedRef.current = true;
          }, 1500);
        }
      } catch {}
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user, tourId]);

  const markTourComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      await axios.post(`${API}/auth/tour-complete`, { tour_id: tourId });
    } catch (err) {
      completedRef.current = false;
    }
  }, [tourId]);

  // Use MutationObserver to detect when tour ends (tooltip removed from DOM)
  useEffect(() => {
    if (!run || !tourStartedRef.current) return;

    let timeoutId = null;
    const observer = new MutationObserver((mutations) => {
      // Check if joyride tooltip still exists
      const tooltip = document.querySelector('.react-joyride__tooltip');
      const overlay = document.querySelector('.react-joyride__overlay');
      
      if (!tooltip && !overlay && tourStartedRef.current && !completedRef.current) {
        // Tour has ended - tooltip removed
        // Use a small delay to avoid false positives during step transitions
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const stillNoTooltip = !document.querySelector('.react-joyride__tooltip');
          if (stillNoTooltip && tourStartedRef.current && !completedRef.current) {
            setRun(false);
            setTourRunning(false);
            markTourComplete();
          }
        }, 500);
      }
    });

    // Start observing after a short delay to let the tour initialize
    const startObserver = setTimeout(() => {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }, 2000);

    return () => {
      clearTimeout(startObserver);
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [run, markTourComplete]);

  const handleCallback = useCallback((data) => {
    const { status, action } = data;

    // Check for tour completion
    const statusStr = String(status || '').toLowerCase();
    if (statusStr === 'finished' || statusStr === 'skipped') {
      setRun(false);
      setTourRunning(false);
      markTourComplete();
      return;
    }

    // Handle close/skip actions
    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      setRun(false);
      setTourRunning(false);
      markTourComplete();
    }
  }, [markTourComplete]);

  // Filter steps based on FRIENDS_MODE_ENABLED flag
  const filteredSteps = MAIN_TOUR_STEPS.filter(step => {
    if (FRIENDS_MODE_ENABLED) return !step.friendsDisabled;
    return !step.friendsOnly;
  });

  if (!loaded || !user) return null;

  return (
    <Joyride
      steps={filteredSteps}
      run={run}
      continuous
      showProgress
      scrollToFirstStep
      disableOverlayClose
      spotlightClicks
      buttons={["back", "close", "primary"]}
      callback={handleCallback}
      styles={TOUR_STYLES}
      locale={{
        back: "Back",
        close: "Got it",
        last: "Let's Go!",
        next: "Next",
        skip: "Skip tour",
      }}
    />
  );
}
